import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Stack,
    Tooltip,
    Chip
} from '@mui/material'
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Event as EventIcon,
    CloudDownload as DownloadIcon
} from '@mui/icons-material'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'
import { toast } from 'react-hot-toast'
import { useLoading } from '../../hooks'
import { holidayApi } from '../../api/holidayApi'
import { formatDate } from '../../utils/formatters'



const HolidayManagement = () => {
    const { loading: listLoading, startLoading: startListLoading, stopLoading: stopListLoading } = useLoading(true)
    const { loading: actionLoading, startLoading: startActionLoading, stopLoading: stopActionLoading } = useLoading()
    const [holidays, setHolidays] = useState([])
    const [openDialog, setOpenDialog] = useState(false)
    const [editingHoliday, setEditingHoliday] = useState(null)

    // Form state
    const [formData, setFormData] = useState({
        date: '',
        name: '',
        type: 'mandatory' // 'mandatory' or 'optional'
    })

    // Fetch holidays
    const fetchHolidays = async () => {
        startListLoading()
        try {
            const response = await holidayApi.getHolidays()
            const formattedHolidays = response.data.map(h => ({
                id: h.id,
                date: h.date,
                name: h.name,
                type: h.holiday_type
            }))
            setHolidays(formattedHolidays)
        } catch (error) {
            console.error('Error fetching holidays:', error)
            toast.error('Failed to load holidays')
        } finally {
            stopListLoading()
        }
    }

    useEffect(() => {
        fetchHolidays()
    }, [])

    const handleOpenDialog = async (holiday = null) => {
        if (holiday) {
            setEditingHoliday(holiday)
            startActionLoading()
            try {
                const response = await holidayApi.getHolidayDetails(holiday.id)
                const detail = response.data
                setFormData({
                    date: detail.date,
                    name: detail.name,
                    type: detail.holiday_type
                })
            } catch (error) {
                console.error('Error fetching holiday details:', error)
                toast.error('Failed to fetch holiday details')
                // Fallback to list data if API fails
                setFormData({
                    date: holiday.date,
                    name: holiday.name,
                    type: holiday.type
                })
            } finally {
                stopActionLoading()
            }
        } else {
            setEditingHoliday(null)
            // Default to today/empty
            setFormData({
                date: new Date().toISOString().split('T')[0],
                name: '',
                type: 'mandatory'
            })
        }
        setOpenDialog(true)
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
        setEditingHoliday(null)
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleSubmit = async () => {
        if (!formData.date || !formData.name) {
            toast.error('Please fill all required fields')
            return
        }

        startActionLoading()
        try {
            if (editingHoliday) {
                const payload = {
                    date: formData.date,
                    name: formData.name,
                    holiday_type: formData.type
                }
                const response = await holidayApi.updateHoliday(editingHoliday.id, payload)
                setHolidays(prev => prev.map(h =>
                    h.id === editingHoliday.id ? {
                        id: response.data.id,
                        date: response.data.date,
                        name: response.data.name,
                        type: response.data.holiday_type
                    } : h
                ))
                toast.success('Holiday updated successfully')
            } else {
                const payload = {
                    date: formData.date,
                    name: formData.name,
                    holiday_type: formData.type
                }
                const response = await holidayApi.addHoliday(payload)
                const newHoliday = {
                    id: response.data.id,
                    date: response.data.date,
                    name: response.data.name,
                    type: response.data.holiday_type
                }
                setHolidays(prev => [...prev, newHoliday])
                toast.success('Holiday added successfully')
            }
            handleCloseDialog()
        } catch (error) {
            console.error('Error saving holiday:', error)
            if (!error.response) {
                toast.error('Failed to save holiday')
            }
        } finally {
            stopActionLoading()
        }
    }

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this holiday?')) return

        startActionLoading()
        try {
            await holidayApi.deleteHoliday(id)
            setHolidays(prev => prev.filter(h => h.id !== id))
            toast.success('Holiday deleted successfully')
        } catch (error) {
            console.error('Error deleting holiday:', error)
            if (!error.response) {
                toast.error('Failed to delete holiday')
            }
        } finally {
            stopActionLoading()
        }
    }

    const handleDownload = async () => {
        try {
            // Generate CSV download
            const headers = ['Date,Name,Type\n']
            const csvContent = holidays.map(h => `${h.date},${h.name},${h.type}`).join('\n')
            const blob = new Blob([headers + csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `Holidays_${new Date().getFullYear()}.csv`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            toast.success('Download started')
        } catch (error) {
            console.error('Error downloading holidays:', error)
            toast.error('Failed to download holiday list')
        }
    }

    return (
        <Box sx={{ p: { xs: 1, md: 3 } }}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold">
                        Yearly Holiday Configuration
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            sx={{ flex: { xs: 1, sm: 'none' } }}
                        >
                            Download List
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                            sx={{ flex: { xs: 1, sm: 'none' } }}
                        >
                            Add Holiday
                        </Button>
                    </Stack>
                </Box>

                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: 'background.default' }}>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Holiday Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {listLoading ? (
                                    <TableSkeleton columns={4} rows={5} />
                                ) : holidays.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <Typography color="textSecondary">
                                                No holidays configured yet
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    holidays.map((holiday) => (
                                        <TableRow key={holiday.id} hover>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <EventIcon color="action" fontSize="small" />
                                                    <Typography>{formatDate(holiday.date)}</Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Typography fontWeight="medium">{holiday.name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={holiday.type === 'mandatory' ? 'Mandatory' : 'Optional'}
                                                    color={holiday.type === 'mandatory' ? 'error' : 'info'}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handleOpenDialog(holiday)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" color="error" onClick={() => handleDelete(holiday.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Add/Edit Dialog */}
                <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
                    <DialogTitle>
                        {editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            <TextField
                                label="Date"
                                type="date"
                                name="date"
                                value={formData.date}
                                onChange={handleInputChange}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                            <TextField
                                label="Holiday Name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                fullWidth
                                required
                                placeholder="e.g. Independence Day"
                            />
                            <TextField
                                select
                                label="Type"
                                name="type"
                                value={formData.type}
                                onChange={handleInputChange}
                                fullWidth
                            >
                                <MenuItem value="mandatory">Mandatory</MenuItem>
                                <MenuItem value="optional">Optional / Restricted</MenuItem>
                            </TextField>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} disabled={actionLoading}>Cancel</Button>
                        <Button onClick={handleSubmit} variant="contained" disabled={actionLoading}>
                            {editingHoliday ? 'Update' : 'Add'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
    )
}

export default HolidayManagement
